import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import type {
  AiKnowledgeBaseImportJobDto,
  AiKnowledgeBaseImportSourceType,
  CreateAiKnowledgeBaseImportRequestDto,
} from '@/shared/api/ai-knowledge-base';

function parseTags(value: string) {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 10);
}

export function KnowledgeBaseImportPanel({
  disabled,
  isImporting,
  lastJob,
  resetToken,
  onImport,
}: {
  disabled: boolean;
  isImporting: boolean;
  lastJob: AiKnowledgeBaseImportJobDto | null;
  resetToken: number;
  onImport: (data: CreateAiKnowledgeBaseImportRequestDto) => void;
}) {
  const { t } = useTranslation('company');
  const [sourceType, setSourceType] =
    useState<AiKnowledgeBaseImportSourceType>('text');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [url, setUrl] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [publishEnabled, setPublishEnabled] = useState(false);
  const tags = useMemo(() => parseTags(tagsText), [tagsText]);
  const sourceReady =
    sourceType === 'text' ? content.trim() !== '' : url.trim() !== '';
  const canImport = !disabled && !isImporting && title.trim() !== '' && sourceReady;

  useEffect(() => {
    if (resetToken < 1) return;
    setTitle('');
    setContent('');
    setUrl('');
    setTagsText('');
    setPublishEnabled(false);
  }, [resetToken]);

  return (
    <Stack data-testid="knowledge-base-import" spacing={1.25} sx={{ minWidth: 0 }}>
      <Stack spacing={0.5}>
        <Typography variant="subtitle2" fontWeight={800}>
          {t('modules.knowledgeBase.import.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('modules.knowledgeBase.import.caption')}
        </Typography>
      </Stack>

      <TextField
        select
        size="small"
        label={t('modules.knowledgeBase.import.sourceType')}
        value={sourceType}
        disabled={disabled || isImporting}
        onChange={(event) =>
          setSourceType(event.target.value as AiKnowledgeBaseImportSourceType)
        }
        sx={{
          '& .MuiInputBase-root': {
            borderRadius: 1,
          },
        }}
      >
        <MenuItem value="text">
          {t('modules.knowledgeBase.import.sourceTypes.text')}
        </MenuItem>
        <MenuItem value="url" disabled>
          {t('modules.knowledgeBase.import.sourceTypes.url')}
        </MenuItem>
      </TextField>

      <TextField
        size="small"
        label={t('modules.knowledgeBase.fields.title')}
        value={title}
        disabled={disabled || isImporting}
        onChange={(event) => setTitle(event.target.value)}
        sx={{
          '& .MuiInputBase-root': {
            borderRadius: 1,
          },
        }}
      />

      {sourceType === 'text' ? (
        <TextField
          size="small"
          multiline
          minRows={4}
          label={t('modules.knowledgeBase.import.content')}
          value={content}
          disabled={disabled || isImporting}
          onChange={(event) => setContent(event.target.value)}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
        />
      ) : (
        <TextField
          size="small"
          label={t('modules.knowledgeBase.import.url')}
          value={url}
          disabled={disabled || isImporting}
          onChange={(event) => setUrl(event.target.value)}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
        />
      )}

      <TextField
        size="small"
        label={t('modules.knowledgeBase.fields.tags')}
        value={tagsText}
        disabled={disabled || isImporting}
        helperText={t('modules.knowledgeBase.tagsHint')}
        onChange={(event) => setTagsText(event.target.value)}
        sx={{
          '& .MuiInputBase-root': {
            borderRadius: 1,
          },
        }}
      />

      <FormControlLabel
        sx={{
          m: 0,
          '& .MuiFormControlLabel-label': {
            fontSize: 14,
            lineHeight: 1.35,
          },
        }}
        control={(
          <Switch
            size="small"
            checked={publishEnabled}
            disabled={disabled || isImporting}
            onChange={(event) => setPublishEnabled(event.target.checked)}
          />
        )}
        label={t('modules.knowledgeBase.import.publishEnabled')}
      />

      <Button
        variant="outlined"
        disabled={!canImport}
        onClick={() => {
          onImport({
            source_type: sourceType,
            title: title.trim(),
            content: sourceType === 'text' ? content.trim() : undefined,
            url: sourceType === 'url' ? url.trim() : undefined,
            tags,
            publish_enabled: publishEnabled,
          });
        }}
        sx={{
          alignSelf: 'flex-start',
          minHeight: 36,
          borderRadius: 1,
          textTransform: 'none',
          fontWeight: 800,
        }}
      >
        {t('modules.knowledgeBase.import.submit')}
      </Button>

      {lastJob ? (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={lastJob.title}
            sx={{ borderRadius: 1 }}
          />
          <Chip
            size="small"
            label={t(`modules.knowledgeBase.import.status.${lastJob.status}`)}
            sx={{ borderRadius: 1 }}
            variant="outlined"
          />
        </Stack>
      ) : null}
    </Stack>
  );
}
